import Backbone from 'backbone'
import { $number, dateStringToMilliseconds, remainDate } from '../utils'
import _ from 'underscore'
import Logger from 'Logger'
import { checkIsGoldClass } from '../config'
import store from './index'

/**
 * @typedef {Object} GoodsModel
 * @property {number} goods_id - 상품 ID
 * @property {string} goods_name - 상품명
 * @property {number} goods_price - 상품 가격
 * @property {?string} goods_descr - 상품 설명
 * @property {string} goods_img_url - 상품 이미지 URL
 * @property {string} price_type - 가격 타입
 * @property {number} order_no - 정렬 순서
 * @property {?string} goods_type - 상품 타입
 * @property {?string} goods_type_dp_start - 상품 타입 노출 시작일
 * @property {?string} goods_type_dp_end - 상품 타입 노출 종료일
 * @property {string} detail_img_url - 상세 이미지 URL
 * @property {string} detail_descr - 상세 설명
 * @property {?string} pop_img_url - 팝업 이미지 URL
 * @property {string} reward_type - 리워드 타입
 * @property {string} eff_code - 효과 코드
 * @property {number} eff_value - 효과 값
 * @property {number} eff_period - 효과 기간
 * @property {string} eff_img_url - 효과 이미지 URL
 * @property {string} package_type - 패키지 타입
 * @property {string} reward_money - 리워드 머니
 * @property {number} reward_ruby - 리워드 루비
 * @property {number} reward_pokerchip - 리워드 포커칩
 * @property {string} receiver_type - 수령자 타입
 * @property {string} icon_type - 아이콘 타입
 * @property {?string} icon_descr - 아이콘 설명
 * @property {?string} goods_path - 상품 경로
 * @property {string} shop_desc - 상점 설명
 * @property {?string} label_type - 라벨 타입
 * @property {?string} label_type_dp_start - 라벨 노출 시작일
 * @property {?string} label_type_dp_end - 라벨 노출 종료일
 * @property {string} store_goods_id - 스토어 상품 ID
 * @property {?string} hotdeal_id - 핫딜 ID
 * @property {?string} start_date - 시작일
 * @property {?string} end_date - 종료일
 * @property {?number} hot_deal_ready_cnt - 핫딜 준비 개수
 * @property {?number} hot_deal_sell_cnt - 핫딜 판매 개수
 * @property {string} goods_disp_name - 상품 표시명 (HTML 포함 가능)
 * @property {number} design_type - 디자인 타입
 * @property {number} detail_view_type - 상세보기 타입 (1: 미노출, 2: 노출, 3: 팝업 노출)
 * @property {string} goods_descr_2 - 추가 상품 설명
 * @property {boolean} auto_payment_goods - 자동 결제 상품 여부
 * @property {?string} landing_url - 랜딩 URL (확률형 상품정보 링크)
 * @property {number[]} vip_types - VIP 타입 배열
 * @property {boolean} isChecked - 선택 여부 (뷰 전용)
 * @property {boolean} isGoldClass - 골드 클래스 여부 (뷰 전용)
 * @property {boolean} disablePmangBonus - 피망 보너스 비활성화 여부 (뷰 전용)
 * @property {boolean} isDetailHide - 상세보기 버튼 노출 여부 (뷰 전용)
 * @property {boolean} isDetailOnlyImage - 상세보기시 이미지 전용 여부 (뷰 전용)
 * @property {boolean} isGoodsNew - 신상품 여부 (뷰 전용)
 * @property {boolean} isDeadLine - 마감 임박 플래그 (뷰 전용)
 * @property {boolean} isSellEnd - 판매 종료 여부 (뷰 전용)
 * @property {boolean} isGift - 선물 가능 여부 (뷰 전용)
 * @property {number|string} remainCount - 남은 아이템 개수 (뷰 전용)
 * @property {number} remainDate - 남은 판매 시간 (뷰 전용)
 * @property {boolean} isShowCounter - 카운터 노출 여부 (뷰 전용)
 * @property {boolean} hasRewardMoney - 리워드 머니 존재 여부 (뷰 전용)
 * @property {boolean} hasVipCoupons - VIP 쿠폰 보유 여부 (뷰 전용)
 */

/**
 * 상품 상세 정보
 * @type Backbone.Model<GoodsModel>
 */
export var GoodsModel = Backbone.Model.extend({
  /**
   *  wiki 참고
   *  https://nwiki.neowiz.com/pages/viewpage.action?pageId=72844112
   */
  defaults: function () {
    return {
      // API가 응답하는 데이터
      goods_id: 0,
      goods_name: '',
      goods_price: 0,
      goods_descr: null,
      goods_img_url: '',
      price_type: '',
      order_no: 0,
      goods_type: null,
      goods_type_dp_start: null,
      goods_type_dp_end: null,
      detail_img_url: '',
      detail_descr: '',
      pop_img_url: null,
      reward_type: '',
      eff_code: '',
      eff_value: 0,
      eff_period: 0,
      eff_img_url: '',
      package_type: '',
      reward_money: '',
      reward_ruby: 0,
      reward_pokerchip: 0,
      receiver_type: '',
      icon_type: '',
      icon_descr: null,
      goods_path: null,
      shop_desc: '',
      label_type: null,
      label_type_dp_start: null,
      label_type_dp_end: null,
      store_goods_id: '',
      hotdeal_id: null,
      start_date: null,
      end_date: null,
      hot_deal_ready_cnt: null,
      hot_deal_sell_cnt: null,
      goods_disp_name: '', // 핫딜 마카오블루 (30일)<em>+27조 5000억 골드 <s>27조 2000억 골드</s></em><span class="poker-mall__text-square">가정의 달 자동응모</span>
      design_type: 0,
      detail_view_type: 0, // 1: 미노출 , 2 노출, 3 팝업 노출
      goods_descr_2: '',
      auto_payment_goods: false,
      landing_url: null, // 확률형 상품정보 링크 PRODPOKER-19342
      vip_types: [
        // 0, 1, 2, 3, 4
      ],

      // view에서 사용하는 추가 속성
      isChecked: false,
      isGoldClass: false,
      disablePmangBonus: false,
      isDetailHide: false, // 아이템 상세보기 버튼 노출 여부
      isDetailOnlyImage: false, // 아이템 상세보기시 설명 이미지만 보이게
      isGoodsNew: false, // 신상품인지 확인
      isDeadLine: false, // 마감 임박 플래그 노출 여부
      isSellEnd: false, // 핫딜 아이템 구매하기 버튼 상태
      isGift: false, // 아이템 선물 가능 여부
      remainCount: 0, // 핫딜 아이템 남은 아이템 갯수
      remainDate: 0, // 핫딜 아이템 남은 판매 시간
      isShowCounter: false, // 핫딜 아이템 준비개수가 0 이면 기간 판매
      hasRewardMoney: false, // 리워드 머니가 0 초과인지. VIP 쿠폰 적용 가능?
      hasVipCoupons: false, // VIP 쿠폰을 가지고 있는지 여부, 각 collection에서 이 모델을 인스턴스할 때 주입
    }
  },

  // API 응답 데이터를 가공하여 뷰에 필요한 데이터로 변환
  parse: function (res) {
    var hotdealSellRatio = res.hot_deal_sell_cnt / res.hot_deal_ready_cnt
    var sellEndDate = dateStringToMilliseconds(res.end_date)
    var hotDealRemainItems = res.hot_deal_ready_cnt - res.hot_deal_sell_cnt
    var hotDealItemUnlimit = res.hot_deal_ready_cnt < 1 // NOTE: hot_deal_ready_cn  핫딜 판매개수가 1 이하 이면 무제한 기간 판매 이다.

    var parsed = _.extend(res, {
      goods_disp_name:
        res.goods_disp_name &&
        res.goods_disp_name.replace(
          /\[(.*?)\](.*?)\[-\]/g,
          "<span class='goods-desc' style='color:#ff7e00'>$2</span>"
        ),

      goods_price_comma: res.goods_price && $number(res.goods_price).comma(),

      goods_descr_2:
        res.goods_descr_2 &&
        res.goods_descr_2.replace('[사용하기]', '<b>[사용하기]</b>'),

      isDetailHide: res.detail_view_type > 1, // 상세보기 설정 1: 미노출 || 2: 노출 || 3: 그림만 노출

      isDetailOnlyImage: res.detail_view_type === 3,

      isGoodsNew: !!res.goods_type,

      isDeadLine:
        (!hotDealItemUnlimit && !res.hotdeal_id && hotdealSellRatio >= 0.9) || // 준비 수량 10% 이하
        (res.hotdeal_id && sellEndDate - _.now() < 86400000), // 판매 시간 1일 이하

      isSellEnd: !hotDealItemUnlimit && res.hotdeal_id && hotdealSellRatio >= 1,

      remainCount:
        hotDealRemainItems > 0
          ? $number(hotDealRemainItems).comma() + '개 남음'
          : '',

      isShowCounter: res.hot_deal_ready_cnt > 0,

      remainDate: res.hotdeal_id && remainDate(res.end_date),

      isGift: res.receiver_type === 'G' || res.receiver_type === 'B', // 선물 가능 한지 여부,

      isGoldClass: checkIsGoldClass(),

      hasRewardMoney: res.reward_money > 0,

      hasVipCoupons: store.list.get('hasVipCoupons'),
    })

    if (res.hotdeal_id) {
      Logger.info(
        'count',
        res.hot_deal_sell_cnt,
        res.hot_deal_ready_cnt,
        hotdealSellRatio >= 0.9
      )
      Logger.info(
        'date string',
        res.end_date,
        sellEndDate,
        sellEndDate - _.now(),
        sellEndDate - _.now() < 86400000
      )
    }

    return parsed
  },
})
